import { useState, ChangeEvent } from 'react';
import { useQuery } from '@apollo/client';

import usePagination from '@hooks/usePagination';
import { qrTableHeader } from '@lib/data';
import { SEE_CREATED_QR_LIST } from '@graphql/queries';
import { Query, QrCreateSequence } from '@graphql/types';

import { FilterBox, Contents } from '@components/styled/StyledComponents';
import Headline from '@components/layout/Headline';
import StyledSelect from '@components/styled/StyledSelect';
import TableHeader from '@components/share/Table/TableHeader';
import { TableBody, EmptyTable } from '@components/share/Table/table.style';
import QrListTableRow from '@components/feature/Setting/Qr/QrListTableRow';
import PublishQRModal from '@components/feature/Setting/Qr/PublishQRModal';
import Pagination from '@components/layout/Pagination';
import Loader from '@components/share/Loader';

const Qr = () => {
    const [addModal, setAddModal] = useState(false);
    // 페이지네이션 관련 상태
    const [take, setTake] = useState(10);
    const [preCursor, setPreCursor] = useState(0);
    const [nextCursor, setNextCursor] = useState(0);
    const [cursorOption, setCursorOption] = useState<boolean | null>(null);
    const [filter, setFilter] = useState({ order: 2 });

    const { data, loading } = useQuery<Pick<Query, 'seeCreatedQrList'>>(
        SEE_CREATED_QR_LIST,
        {
            variables: {
                orderBy: +filter.order,
                take,
                preCursor,
                nextCursor,
                cursorOption
            },
            onError: (err) => {
                console.error(err);
            }
        }
    );

    const createQrList = data?.seeCreatedQrList?.QRCreateSequenceList ?? [];
    const totalLength = data?.seeCreatedQrList?.totalLength ?? 0;

    const onFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilter((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // 페이지네이션
    const {
        pageIndex,
        setPageIndex,
        pageGroupLength,
        prevPageHandler,
        nextPageHandler,
        prevPageGroupHandler,
        nextPageGroupHandler,
        resetPaginationHandler,
        disabled
    } = usePagination(
        take,
        totalLength,
        setPreCursor,
        setNextCursor,
        setCursorOption,
        createQrList,
        'qrcsId'
    );

    return (
        <Contents>
            <Headline
                title="QR 관리"
                subTitle="펫이닷 반려동물 QR 관리"
                searchCount={totalLength}
                buttonList={['QR 발행하기']}
                buttonAction={[() => setAddModal(true)]}
            />
            {/*  필터 영역 */}
            <FilterBox>
                <StyledSelect
                    title="정렬"
                    name="order"
                    value={filter.order}
                    onChange={onFilterChange}
                    options={[
                        { title: '내림차순', value: 1 },
                        { title: '오름차순', value: 2 }
                    ]}
                    DATA_VALUE
                />
            </FilterBox>
            <TableHeader header={qrTableHeader} />

            <TableBody $minHeight={500}>
                {loading && <Loader />}
                {totalLength === 0 ? (
                    <EmptyTable>발행된 QR 코드가 없습니다.</EmptyTable>
                ) : (
                    createQrList
                        .slice(pageIndex, pageIndex + take)
                        .map((data, idx) => (
                            <QrListTableRow
                                key={`${data?.qrcsId}-qr-list`}
                                header={qrTableHeader}
                                data={data as QrCreateSequence}
                            />
                        ))
                )}
            </TableBody>

            {totalLength > 0 && (
                <Pagination
                    take={take}
                    setTake={setTake}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageGroupLength={pageGroupLength}
                    totalLength={totalLength}
                    prevPageHandler={prevPageHandler}
                    nextPageHandler={nextPageHandler}
                    prevPageGroupHandler={prevPageGroupHandler}
                    nextPageGroupHandler={nextPageGroupHandler}
                    resetPaginationHandler={resetPaginationHandler}
                    disabled={disabled}
                />
            )}
            {addModal && (
                <PublishQRModal isOpen={addModal} setIsOpen={setAddModal} />
            )}
        </Contents>
    );
};

export default Qr;
